import type { TextInputProps } from '@ksyos/design-system/dist/components/text-input';
import type { Dispatch } from 'react';
import { Col, FormErrorMessage, Row, Text, TextInput } from '@ksyos/design-system';
import { useState } from 'react';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import type { CareProviderOrganizationsPractitioners, InitialOrganizationPractitioner } from './CareProviderSelection';
import { translateKey } from '../../translate';
import { computeTextInputProps } from '../fields/TextInputField';
import { GeneralPractitionerSearchResultForm } from './GeneralPractitionerSearchResultForm';
import { GeneralPractitionerSearchTableBody } from './GeneralPractitionerSearchTableBody';

interface IGeneralPractitionerSearchProps {
    clearSelectedItem: () => Promise<void>;
    error?: string;
    initialOrganizationId: string | null;
    initialOrganizationPractitioner?: InitialOrganizationPractitioner;
    initialPractitionerId: string | null;
    itemsPerPage?: number;
    onChange: (value?: string) => Promise<void>;
    onSearch: (searchName?: string, searchCity?: string, page?: string) => Promise<void>;
    organizationPractitioners?: CareProviderOrganizationsPractitioners[];
    total: number;
}

export function GeneralPractitionerSearch(props: IGeneralPractitionerSearchProps) {
    const [searchCity, setSearchCity] = useState<string>();
    const [searchName, setSearchName] = useState<string>();

    return (
        <>
            <Row
                align="center"
                rowGap={2}
                wrap="wrap"
            >
                <Col span={[12, 5]}>
                    <TextInput
                        {...getInputTextProps(
                            async () => await onSearch(undefined, searchCity),
                            translateKey('common.searchName'),
                            setSearchName,
                            searchName
                        )}
                    />
                </Col>
                <Col span={[12, 2]}>
                    <Text
                        size="sm"
                        textAlign="center"
                    >
                        {translateKey('common.andOr')}
                    </Text>
                </Col>
                <Col span={[12, 5]}>
                    <TextInput
                        {...getInputTextProps(
                            async () => await onSearch(searchName),
                            translateKey('common.searchCity'),
                            setSearchCity,
                            searchCity
                        )}
                    />
                </Col>
            </Row>

            {props.error && <FormErrorMessage>{getValidationErrorTranslation(props.error)}</FormErrorMessage>}

            {(props.organizationPractitioners || props.initialOrganizationPractitioner) && (
                <GeneralPractitionerSearchResultForm
                    hidePagination={!props.organizationPractitioners}
                    itemsPerPage={props.itemsPerPage}
                    onSearch={props.onSearch}
                    searchCity={searchCity}
                    searchName={searchName}
                    showNoResults={Boolean(
                        props.organizationPractitioners && props.organizationPractitioners.length === 0
                    )}
                    total={props.total}
                >
                    {(props.organizationPractitioners || props.initialOrganizationPractitioner) && (
                        <GeneralPractitionerSearchTableBody
                            initialOrganizationId={props.initialOrganizationId}
                            initialOrganizationPractitioner={props.initialOrganizationPractitioner}
                            initialPractitionerId={props.initialPractitionerId}
                            onChange={props.onChange}
                            organizationPractitioners={props.organizationPractitioners}
                        />
                    )}
                </GeneralPractitionerSearchResultForm>
            )}
        </>
    );

    function getInputTextProps(
        onReset: () => Promise<void>,
        placeholder: string,
        setValue: Dispatch<React.SetStateAction<string | undefined>>,
        value: string | undefined
    ): TextInputProps {
        return computeTextInputProps({
            onChange: async (targetValue) => setValue(targetValue || undefined),
            onEnterKeyPress: async () => await onSearch(searchName, searchCity),
            onReset: async () => {
                await props.clearSelectedItem();
                await onReset();
                setValue(undefined);
            },
            placeholder,
            suffix: {
                icon: 'search',
                labelText: 'search',
                onSuffixClick: async () => await onSearch(searchName, searchCity),
            },
            value: value || null,
            variant: 'outline',
        });
    }

    async function onSearch(name?: string, city?: string) {
        await props.onSearch(name, city, '1');
    }
}
